/**
 * The honest placeholder.
 *
 * Every surface renders this until its real interface arrives. It deliberately
 * does not draw a plausible shell with an empty table and a disabled button,
 * because a screenshot of that is indistinguishable from working software and
 * ends up in a deck. It says what the surface is, that it is not built, and
 * which stage builds it.
 */

export interface SurfacePlaceholderProps {
  /** The surface name, as it appears in section 18. */
  surface: string;
  /** One line on who it is for and how they reach it. */
  audience: string;
  /** The build-order stage that replaces this. */
  stage: string;
  /** What arrives with that stage. */
  arriving: readonly string[];
}

export function SurfacePlaceholder({
  surface,
  audience,
  stage,
  arriving,
}: SurfacePlaceholderProps): React.JSX.Element {
  return (
    <main className="placeholder">
      <p className="placeholder__eyebrow">Digital Document Distribution System</p>
      <h1 className="placeholder__title">{surface}</h1>
      <p className="placeholder__audience">{audience}</p>

      <p className="placeholder__status" role="status">
        Not implemented. This surface is built in {stage}.
      </p>

      <section className="placeholder__arriving">
        <h2>What arrives with {stage}</h2>
        <ul>
          {arriving.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <footer className="placeholder__footer">
        Specification: <code>docs/self-hosted/engineering.html</code>. Progress: <code>PLAN.md</code>.
      </footer>
    </main>
  );
}
